import { createFaceLandmarkerWorker } from "./FaceLandmarkerWorkerFactory";
import { LIVENESS_CONFIG } from "./livenessConfig";

const createFaceDetectionError = (message) => {
  const error = new Error(message);
  error.name = "FaceDetectionError";
  return error;
};

export class FaceLandmarkerClient {
  constructor({ workerFactory = createFaceLandmarkerWorker } = {}) {
    this.workerFactory = workerFactory;
    this.worker = null;
    this.ready = false;
    this.initializationPromise = null;
    this.initializationHandlers = null;
    this.pendingRequests = new Map();
    this.nextRequestId = 1;
  }

  initialize() {
    if (this.ready) return Promise.resolve();
    if (this.initializationPromise) return this.initializationPromise;

    try {
      this.worker = this.workerFactory();
    } catch (error) {
      return Promise.reject(
        createFaceDetectionError(
          error?.message || "Face detection could not start.",
        ),
      );
    }

    this.worker.addEventListener("message", this.handleMessage);
    this.worker.addEventListener("error", this.handleWorkerError);

    this.initializationPromise = new Promise((resolve, reject) => {
      const rejectInitialization = (error) => {
        window.clearTimeout(timeoutId);
        this.initializationPromise = null;
        this.initializationHandlers = null;
        this.stopWorker();
        reject(error);
      };
      const timeoutId = window.setTimeout(() => {
        rejectInitialization(
          createFaceDetectionError("Face detection initialization timed out."),
        );
      }, LIVENESS_CONFIG.initializationTimeoutMs);

      this.initializationHandlers = {
        resolve: () => {
          window.clearTimeout(timeoutId);
          this.ready = true;
          this.initializationHandlers = null;
          resolve();
        },
        reject: rejectInitialization,
      };
    });

    this.worker.postMessage({ type: "initialize" });
    return this.initializationPromise;
  }

  async detect(imageData, timestamp) {
    await this.initialize();
    const requestId = this.nextRequestId;
    this.nextRequestId += 1;

    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(createFaceDetectionError("Face detection timed out."));
      }, LIVENESS_CONFIG.detectionTimeoutMs);

      this.pendingRequests.set(requestId, { resolve, reject, timeoutId });
      // 直接转移像素缓冲区，避免每一帧都复制一份完整图像数据。
      this.worker.postMessage(
        { type: "detect", requestId, imageData, timestamp },
        [imageData.data.buffer],
      );
    });
  }

  handleMessage = ({ data }) => {
    if (data?.type === "ready") {
      this.initializationHandlers?.resolve();
      return;
    }

    if (data?.type === "error" && !data.requestId) {
      this.initializationHandlers?.reject(
        createFaceDetectionError(data.message),
      );
      return;
    }

    const request = this.pendingRequests.get(data?.requestId);
    if (!request) return;

    window.clearTimeout(request.timeoutId);
    this.pendingRequests.delete(data.requestId);

    if (data.type === "result") {
      request.resolve(data.pose);
    } else {
      request.reject(createFaceDetectionError(data.message));
    }
  };

  handleWorkerError = (event) => {
    const workerMessage = event?.error?.message || event?.message;
    const error = createFaceDetectionError(
      workerMessage
        ? `Face detection worker failed: ${workerMessage}`
        : "Face detection stopped working.",
    );
    this.initializationHandlers?.reject(error);
    this.pendingRequests.forEach(({ reject, timeoutId }) => {
      window.clearTimeout(timeoutId);
      reject(error);
    });
    this.pendingRequests.clear();
    this.initializationPromise = null;
    this.initializationHandlers = null;
    this.stopWorker();
  };

  stopWorker() {
    this.worker?.removeEventListener("message", this.handleMessage);
    this.worker?.removeEventListener("error", this.handleWorkerError);
    this.worker?.terminate();
    this.worker = null;
    this.ready = false;
  }

  dispose() {
    this.worker?.postMessage({ type: "dispose" });
    this.pendingRequests.forEach(({ reject, timeoutId }) => {
      window.clearTimeout(timeoutId);
      reject(createFaceDetectionError("Face detection was stopped."));
    });
    this.pendingRequests.clear();
    this.stopWorker();
    this.initializationPromise = null;
    this.initializationHandlers = null;
  }
}

let sharedClient = null;

// 准备页面可以提前初始化这个共享 Worker。用户打开摄像头后，
// FaceCapture 会直接复用，避免重复加载 WASM 和人脸模型。
export const getFaceLandmarkerClient = () => {
  if (!sharedClient) sharedClient = new FaceLandmarkerClient();
  return sharedClient;
};

export const preloadFaceLandmarker = () =>
  getFaceLandmarkerClient().initialize();
